import { friendlyError } from "@/api/friendlyError";

export interface VoiceToken {
  token: string;
  url: string;
  room: string;
  identity: string;
}

export interface RequestVoiceTokenArgs {
  baseUrl: string;
  room: string;
  identity?: string;
  /** Bearer token (embed userJWT) — cross-origin embeds have no session
   *  cookie, so the voice_service verifies this header instead. */
  authToken?: string;
  fetchImpl?: typeof globalThis.fetch;
}

/** Fetch a LiveKit room token from the voice_service. The room must match
 *  ^voice-[a-zA-Z0-9-]+$ (the service validates it). The returned token
 *  embeds the user's auth + tts voice as participant metadata, so the
 *  worker can authenticate the downstream /v1/chat calls.
 *
 *  Auth: the voice_service is fronted by nginx same-origin (contract §20), so
 *  the HttpOnly `egpt_session` cookie rides automatically via credentials. */
export async function requestVoiceToken(args: RequestVoiceTokenArgs): Promise<VoiceToken> {
  const fetchImpl = args.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = args.baseUrl.replace(/\/+$/, "");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (args.authToken) headers["Authorization"] = `Bearer ${args.authToken}`;
  const resp = await fetchImpl(`${base}/token`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ room: args.room, identity: args.identity }),
  });
  if (!resp.ok) {
    throw new Error(friendlyError(resp.status));
  }
  return (await resp.json()) as VoiceToken;
}

/** Generate a fresh, contract-valid room id. */
export function newVoiceRoom(): string {
  return `voice-${crypto.randomUUID()}`;
}
